import {useCallback, useEffect, useState} from 'react';
import type {SearchKeywordItemResponse} from '../../../core/entities/search/schema';
import {getSearchKeywordRepository} from '../../../core/repositories/searchKeywordRepository';

export function useSearchKeyword(
  searchWord: string,
  typeString?: string,
  page?: number,
) {
  const [searchResult, setSearchResult] = useState<SearchKeywordItemResponse[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [resultCount, setResultCount] = useState<number>(0);
  const [recommendKeywords, setRecommendKeywords] = useState<string[]>([]);
  const [nextPage, setNextPage] = useState<number>(1);
  const [type, setType] = useState<string>('');

  const getSearchKeyword = async (
    keyword: string,
    typeString: string,
    page?: number,
  ) => {
    setIsLoading(true);
    setType(typeString);
    if (type !== typeString) {
      setSearchResult([]);
      setResultCount(0);
      setRecommendKeywords([]);
    }

    try {
      const result = await getSearchKeywordRepository({
        keyword,
        typeString,
        page,
      });
      if (
        result.ok &&
        result.value.item !== null &&
        result.value.item !== undefined
      ) {
        setSearchResult(result.value.item);
        setResultCount(result.value.resultCount);
        setNextPage(result.value.nextPage);
        setRecommendKeywords(result.value.recommendKeywords);
      }
    } catch (error) {
      console.error(error);
    }

    setIsLoading(false);
  };

  const getSearchKeywordNextPage = useCallback(
    async (searchWord: string, type: string, nextPage: number) => {
      setIsLoading(true);
      const previousScrollPosition = window.scrollY;
      try {
        const result = await getSearchKeywordRepository({
          keyword: searchWord,
          typeString: type,
          page: nextPage,
        });
        if (
          result.ok &&
          result.value.item !== null &&
          result.value.item !== undefined
        ) {
          setSearchResult((prev: SearchKeywordItemResponse[]) => [
            ...prev,
            ...(result.value.item as SearchKeywordItemResponse[]),
          ]);
          setResultCount(result.value.resultCount);
          setNextPage(result.value.nextPage);
          setRecommendKeywords(result.value.recommendKeywords);
        }
      } catch (error) {
        console.error(error);
      }

      setIsLoading(false);
      setTimeout(() => {
        window.scrollTo(0, previousScrollPosition);
      }, 100);
    },
    [searchWord, nextPage, typeString],
  );

  useEffect(() => {
    if (searchWord) {
      void getSearchKeyword(searchWord, typeString ?? '', page);
    }
  }, [typeString, page]);

  return {
    getSearchKeyword,
    getSearchKeywordNextPage,
    isLoading,
    searchResult,
    resultCount,
    recommendKeywords,
    nextPage,
    type,
  };
}
